export const RECENT_SEARCHES = 'RECENT_SEARCHES';
const PAST_LOCATIONS = 'PAST_LOCATIONS';
export const BLOCK_GEOLOCATION = 'BLOCK_GEOLOCATION';
export const SAVINGS_CARD = 'SAVINGS_CARD';
export const RECENT_PHARMACY_SEARCHES = 'RECENT_PHARMACY_SEARCHES';
export const RECENT_PROVIDER_SEARCHES = 'RECENT_PROVIDER_SEARCHES';
export const RECENT_PRACTICE_SEARCHES = 'RECENT_PRACTICE_SEARCHES';
export const RECENT_FACILITY_SEARCHES = 'RECENT_FACILITY_SEARCHES';

const get = (key: string): string | null => {
  if (typeof localStorage !== 'undefined') {
    return localStorage.getItem(key);
  }
  return null;
};

const set = (key: string, value: string): void => {
  if (typeof localStorage !== 'undefined') {
    return localStorage.setItem(key, value);
  }
};

const clear = (key: string): void => {
  if (typeof localStorage !== 'undefined') {
    return localStorage.removeItem(key);
  }
};

type SearchResult = {
  name: string;
  formalName: string;
  slug: string;
  genericSlug: string;
};

type LocationCenter = [number, number];

type PastLocation = {
  id: string;
  place_name: string;
  center: LocationCenter;
};

type SearchItemsStorage<T> = {
  get: (limit: number) => Array<T>;
  set: (items: Array<T>) => void;
  add: (item: T) => void;
  remove: (item: T) => void;
  clear: () => void;
};

function CreateSearchItemsStorage<T>(key: string, identifier: (item: T) => string | number): SearchItemsStorage<T> {
  const getItems = (limit = 5): Array<T> => {
    const result = get(key);
    if (result) {
      try {
        return JSON.parse(result).slice(0, limit);
      } catch (e) {
        if (e instanceof SyntaxError) {
          return [];
        }
        throw e;
      }
    }
    return [];
  };
  const setItems = (items: Array<T>): void => set(key, JSON.stringify(items));
  const addItem = (item: T): void => {
    const items = getItems();
    const currIndex = items.findIndex((curr: T) => identifier(curr) === identifier(item));
    if (currIndex !== -1) {
      items.splice(currIndex, 1);
    }
    items.unshift(item);
    setItems(items);
  };
  const removeItem = (item: T): void => {
    const items = getItems();
    const currIndex = items.findIndex((curr: T) => identifier(curr) === identifier(item));
    if (currIndex !== -1) {
      items.splice(currIndex, 1);
    }
    setItems(items);
  };
  const clearItems = (): void => {
    return clear(key);
  };

  return {
    get: getItems,
    set: setItems,
    add: addItem,
    remove: removeItem,
    clear: clearItems,
  };
}

// Id is shared with addresses that are really close, using placename to make it more unique
export const PastLocations = CreateSearchItemsStorage(
  PAST_LOCATIONS,
  (item: PastLocation) => `${item.place_name}${item.id}`
);
export const RecentSearches = CreateSearchItemsStorage(RECENT_SEARCHES, (item: SearchResult) => item.slug);
export const BlockGeolocation = {
  get: (): boolean => get(BLOCK_GEOLOCATION) === 'true',
  set: (value: boolean): void => set(BLOCK_GEOLOCATION, String(value)),
};
export const StoredCard = {
  get: (): string | null => {
    const card = get(SAVINGS_CARD);
    if (card?.indexOf('SVECARD') !== -1) return null;
    return card;
  },
  set: (value: string): void => set(SAVINGS_CARD, value),
  clear: (): void => {
    clear(SAVINGS_CARD);
  },
};

type PharmacySearchResult = {
  name: string;
  slug: string;
  count: number;
  location: string;
};

export const RecentPharmacySearches = CreateSearchItemsStorage(
  RECENT_PHARMACY_SEARCHES,
  (item: PharmacySearchResult) => item.slug
);

type ProviderSearchResult = {
  name: string;
  subtitle: string;
  url: string;
};

export const RecentProviderSearches = CreateSearchItemsStorage(
  RECENT_PROVIDER_SEARCHES,
  (item: ProviderSearchResult) => item.url
);

export const RecentPracticeSearches = CreateSearchItemsStorage(
  RECENT_PRACTICE_SEARCHES,
  (item: ProviderSearchResult) => item.url
);

export const RecentFacilitySearches = CreateSearchItemsStorage(
  RECENT_FACILITY_SEARCHES,
  (item: ProviderSearchResult) => item.url
);
